import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Check, Info } from 'lucide-react';
import { apiService } from '../../services/apiService';

interface AIModel {
  id: string;
  providerModelId: string;
  name: string;
  provider: string;
  description?: string;
  price_per_token?: string;
  capabilities?: Record<string, number>;
  requires_premium?: boolean;
  contextWindow?: number;
  maxTokens?: number;
  supports_vision?: boolean;
  supports_streaming?: boolean;
  functionCalling?: boolean;
  taskDeterminationModel?: boolean;
  task_determination_priority?: number;
  category?: string;
  ranked_position?: number;
  normalizedCost?: number;
  isUncensored?: boolean;
  isPrivacyCompliant?: boolean;
  isCustomEndpoint?: boolean;
  customEndpointUrl?: string;
  customApiKey?: string;
  customHeaders?: Record<string, string>;
}

interface ModelWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (model: AIModel) => Promise<string | null>;
  isAdmin: boolean;
  existingModels?: AIModel[];
}

type WizardStep = 'essentials' | 'capabilities' | 'endpoint' | 'review';

export default function ModelWizard({ isOpen, onClose, onSave, isAdmin, existingModels = [] }: ModelWizardProps) {
  // Calculate next rank position (highest + 1)
  const getNextRankPosition = () => {
    if (existingModels.length === 0) return 1;
    const maxRank = Math.max(...existingModels.map(m => m.ranked_position || 0));
    return maxRank + 1;
  };
  const [currentStep, setCurrentStep] = useState<WizardStep>('essentials');
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [model, setModel] = useState<AIModel>({
    id: '',
    providerModelId: '',
    name: '',
    provider: isAdmin ? 'openrouter' : 'custom',
    description: '',
    price_per_token: '',
    capabilities: {
      webSearch: 0,
      enhancedReasoning: 0,
      reasoning: 0,
      coding: 0,
      creativity: 0,
      conversational: 0,
      speed: 0,
      imageGeneration: 0
    },
    contextWindow: 8192,
    maxTokens: 4096,
    supports_vision: false,
    supports_streaming: true,
    functionCalling: false,
    taskDeterminationModel: false,
    task_determination_priority: 0,
    category: '',
    ranked_position: getNextRankPosition(),
    normalizedCost: 0,
    requires_premium: false,
    isUncensored: false,
    isPrivacyCompliant: false,
    isCustomEndpoint: !isAdmin,
    customEndpointUrl: '',
    customApiKey: '',
    customHeaders: {}
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('essentials');
      setError('');
      setConnectionStatus('idle');
    }
  }, [isOpen]);

  const steps: WizardStep[] = model.provider === 'custom'
    ? ['essentials', 'capabilities', 'endpoint', 'review']
    : ['essentials', 'capabilities', 'review'];

  const currentStepIndex = steps.indexOf(currentStep);
  const totalSteps = steps.length;

  const handleFieldChange = (field: string, value: any) => {
    let processedValue = value;

    if (field === 'providerModelId') {
      setModel(prev => ({ ...prev, id: value, providerModelId: value }));
      return;
    }

    if (field === 'provider') {
      const isCustom = value === 'custom';
      setModel(prev => ({
        ...prev,
        provider: value,
        isCustomEndpoint: isCustom
      }));
      return;
    }

    setModel(prev => ({ ...prev, [field]: processedValue }));
  };

  const handleCapabilityChange = (capability: string, value: number) => {
    setModel(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        [capability]: value
      }
    }));
  };

  const rankConflict = model.ranked_position != null
    ? existingModels.find(m => m.ranked_position === model.ranked_position)
    : null;

  const isDuplicateModel = !!(model.providerModelId && model.customEndpointUrl &&
    existingModels.some(
      m => m.providerModelId === model.providerModelId && m.customEndpointUrl === model.customEndpointUrl
    ));

  const canProceed = () => {
    switch (currentStep) {
      case 'essentials':
        return model.name && model.providerModelId && model.provider && !rankConflict;
      case 'capabilities':
        return true; // Capabilities can be 0
      case 'endpoint':
        return model.customEndpointUrl && model.customApiKey;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!canProceed()) {
      setError('Please fill in all required fields');
      return;
    }

    setError('');
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < totalSteps) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const handlePrevious = () => {
    setError('');
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  const handleTestConnection = async () => {
    if (!model.customEndpointUrl || !model.customApiKey) {
      setError('Please provide both endpoint URL and API key');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');
    setError('');

    try {
      const response = await apiService.post('/portal/models/test-connection', {
        url: model.customEndpointUrl,
        apiKey: model.customApiKey,
        headers: model.customHeaders,
        modelId: model.providerModelId || 'test'
      });

      setConnectionStatus('success');
    } catch (err: any) {
      setConnectionStatus('error');
      const errorMessage = err.response?.data?.error || err.message || 'Connection failed';
      setError(errorMessage);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!canProceed()) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    setError('');

    const errorMessage = await onSave(model);

    setSaving(false);

    if (!errorMessage) {
      onClose();
    } else {
      setError(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 !m-0">
      <div className="bg-white rounded-lg w-full max-w-3xl h-[95vh] overflow-hidden flex flex-col relative z-[10000]">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Add New Model</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Step {currentStepIndex + 1} of {totalSteps}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-4 pt-3">
          <div className="flex items-center justify-between mb-1">
            {steps.map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      index < currentStepIndex
                        ? 'bg-green-500 text-white'
                        : index === currentStepIndex
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {index < currentStepIndex ? <Check size={20} /> : index + 1}
                  </div>
                  <span className="text-xs mt-1 text-gray-600 capitalize">
                    {step === 'essentials' ? 'Essentials' : step}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    index < currentStepIndex ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-4 mt-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {currentStep === 'essentials' && (
            <EssentialsStep
              model={model}
              onFieldChange={handleFieldChange}
              isAdmin={isAdmin}
              nextRankPosition={getNextRankPosition()}
              rankConflict={rankConflict}
            />
          )}

          {currentStep === 'capabilities' && (
            <CapabilitiesStep
              capabilities={model.capabilities || {}}
              onCapabilityChange={handleCapabilityChange}
            />
          )}

          {currentStep === 'endpoint' && (
            <EndpointStep
              model={model}
              onFieldChange={handleFieldChange}
              onTestConnection={handleTestConnection}
              isDuplicate={isDuplicateModel}
              testingConnection={testingConnection}
              connectionStatus={connectionStatus}
              error={error}
            />
          )}

          {currentStep === 'review' && (
            <ReviewStep model={model} />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handlePrevious}
            disabled={currentStepIndex === 0}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Previous
          </button>

          <div className="flex gap-2">
            {currentStepIndex < totalSteps - 1 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Next Step
                <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={!canProceed() || saving}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? 'Saving...' : 'Save Model'}
                <Check size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step Components
function EssentialsStep({ model, onFieldChange, isAdmin, nextRankPosition, rankConflict }: any) {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  const tooltips: Record<string, string> = {
    name: 'A friendly display name for this model',
    providerModelId: 'Unique identifier used by the provider (e.g., gpt-4, claude-3-sonnet)',
    contextWindow: 'Maximum number of tokens the model can process in input',
    maxTokens: 'Maximum number of tokens the model can generate in output',
    category: 'Model category for filtering and organization',
    ranked_position: 'Global ranking position (1 = best)',
    price_per_token: 'Pricing format: "$1.25/1M in, $10/1M out" or "free". System averages input and output costs for price priority sorting.',
    vision: 'Model can process and understand images',
    streaming: 'Model supports real-time streaming responses',
    functionCalling: 'Model supports OpenAI-compatible tool/function calling (required for tools like OpenClaw)',
    privacy: 'Model is privacy-compliant for sensitive data',
    uncensored: 'Model handles sensitive content without restrictions',
    taskDet: 'Use this model for task determination decisions',
    premium: 'Model requires premium subscription',
  };

  const InfoIcon = ({ field, position = "below" }: { field: string; position?: "above" | "below" }) => (
    <div className="relative inline-block ml-1">
      <Info
        size={14}
        className="text-gray-400 hover:text-gray-600 cursor-help inline-block"
        onMouseEnter={() => setShowTooltip(field)}
        onMouseLeave={() => setShowTooltip(null)}
      />
      {showTooltip === field && (
        <div className={`absolute ${position === "above" ? "right-0 bottom-6" : "left-0 top-6"} z-[60] w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg`}>
          {tooltips[field]}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Let's start with the basics</h3>
        <p className="text-sm text-gray-500 mb-3">
          Tell us about your model. We'll use this information to identify and route requests.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model Name *
              <InfoIcon field="name" />
            </label>
            <input
              type="text"
              value={model.name}
              onChange={(e) => onFieldChange('name', e.target.value)}
              placeholder="GPT-4 Turbo"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider Model ID *
              <InfoIcon field="providerModelId" />
            </label>
            <input
              type="text"
              value={model.providerModelId}
              onChange={(e) => onFieldChange('providerModelId', e.target.value)}
              placeholder="gpt-4-turbo"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
                <InfoIcon field="category" />
              </label>
              <select
                value={model.category}
                onChange={(e) => onFieldChange('category', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="general">General</option>
                <option value="reasoning">Reasoning</option>
                <option value="coding">Coding</option>
                <option value="creative">Creative</option>
                <option value="vision">Vision</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rank Position
                <InfoIcon field="ranked_position" />
              </label>
              <input
                type="number"
                value={model.ranked_position}
                onChange={(e) => onFieldChange('ranked_position', parseInt(e.target.value))}
                placeholder={nextRankPosition.toString()}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                  rankConflict
                    ? 'border-red-400 focus:ring-red-300'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {rankConflict && (
                <p className="text-xs text-red-600 mt-1">
                  Rank {model.ranked_position} is already used by "{rankConflict.name}". Choose a different rank.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Context Window
                <InfoIcon field="contextWindow" />
              </label>
              <input
                type="number"
                value={model.contextWindow}
                onChange={(e) => onFieldChange('contextWindow', parseInt(e.target.value))}
                placeholder="8192"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Tokens
                <InfoIcon field="maxTokens" />
              </label>
              <input
                type="number"
                value={model.maxTokens}
                onChange={(e) => onFieldChange('maxTokens', parseInt(e.target.value))}
                placeholder="4096"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price/Token
              <InfoIcon field="price_per_token" />
            </label>
            <input
              type="text"
              value={model.price_per_token}
              onChange={(e) => onFieldChange('price_per_token', e.target.value)}
              placeholder="$1.25/1M in, $10/1M out or Free"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider *
            </label>
            <div className="grid grid-cols-2 gap-2">
              {isAdmin && (
                <>
                  <ProviderCard
                    value="openrouter"
                    label="OpenRouter"
                    selected={model.provider === 'openrouter'}
                    onClick={() => onFieldChange('provider', 'openrouter')}
                    icon="🔀"
                  />
                  <ProviderCard
                    value="google"
                    label="Google"
                    selected={model.provider === 'google'}
                    onClick={() => onFieldChange('provider', 'google')}
                    icon="☁️"
                  />
                  <ProviderCard
                    value="venice"
                    label="Venice"
                    selected={model.provider === 'venice'}
                    onClick={() => onFieldChange('provider', 'venice')}
                    icon="🎭"
                  />
                </>
              )}
              <ProviderCard
                value="custom"
                label="Custom"
                selected={model.provider === 'custom'}
                onClick={() => onFieldChange('provider', 'custom')}
                icon="🔌"
                subtitle={!isAdmin ? "Your own API" : undefined}
              />
            </div>
            {!isAdmin && (
              <p className="text-xs text-gray-500 mt-1">
                Regular users can only add custom endpoint models
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={model.description}
              onChange={(e) => onFieldChange('description', e.target.value)}
              placeholder="Latest GPT-4 model with improved performance and longer context..."
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.supports_vision}
                onChange={(e) => onFieldChange('supports_vision', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Vision
                <InfoIcon field="vision" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.supports_streaming}
                onChange={(e) => onFieldChange('supports_streaming', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Streaming
                <InfoIcon field="streaming" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.functionCalling || false}
                onChange={(e) => onFieldChange('functionCalling', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Function Calling
                <InfoIcon field="functionCalling" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.isPrivacyCompliant}
                onChange={(e) => onFieldChange('isPrivacyCompliant', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Privacy
                <InfoIcon field="privacy" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.isUncensored}
                onChange={(e) => onFieldChange('isUncensored', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Uncensored
                <InfoIcon field="uncensored" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.taskDeterminationModel}
                onChange={(e) => onFieldChange('taskDeterminationModel', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Task Det
                <InfoIcon field="taskDet" position="above" />
              </span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={model.requires_premium}
                onChange={(e) => onFieldChange('requires_premium', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center">
                Premium
                <InfoIcon field="premium" position="above" />
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderCard({ value, label, selected, onClick, icon, subtitle }: any) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-4 border-2 rounded-lg transition-all ${
        selected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="text-3xl mb-1">{icon}</div>
      <div className="font-medium text-sm">{label}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
    </button>
  );
}

function CapabilitiesStep({ capabilities, onCapabilityChange }: any) {
  const capabilityConfig = [
    { key: 'reasoning', label: 'Reasoning', icon: '🧠', description: 'Logical analysis and problem-solving' },
    { key: 'coding', label: 'Coding', icon: '💻', description: 'Programming and code generation' },
    { key: 'creativity', label: 'Creativity', icon: '🎨', description: 'Creative writing and ideation' },
    { key: 'conversational', label: 'Conversational', icon: '💬', description: 'Natural dialogue and chat' },
    { key: 'speed', label: 'Speed', icon: '⚡', description: 'Response time and efficiency' },
    { key: 'webSearch', label: 'Web Search', icon: '🔍', description: 'Internet search capabilities' },
    { key: 'enhancedReasoning', label: 'Enhanced Reasoning', icon: '🤔', description: 'Deep analytical thinking' },
    { key: 'imageGeneration', label: 'Image Generation', icon: '🖼️', description: 'Creating images from text' }
  ];

  const leftColumn = capabilityConfig.slice(0, 4);
  const rightColumn = capabilityConfig.slice(4);

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">How capable is this model?</h3>
        <p className="text-sm text-gray-500 mb-3">
          Rate each capability from 0 (not capable) to 5 (excellent). This helps us route requests to the best model.
        </p>
      </div>

      <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          💡 <strong>Tip:</strong> Set capabilities that match the model's strengths. Lower scores are okay - they help us choose the right model for each task.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-3">
          {leftColumn.map(({ key, label, icon, description }) => (
            <CapabilitySlider
              key={key}
              label={label}
              icon={icon}
              description={description}
              value={capabilities[key] || 0}
              onChange={(value) => onCapabilityChange(key, value)}
            />
          ))}
        </div>

        {/* Right Column */}
        <div className="space-y-3">
          {rightColumn.map(({ key, label, icon, description }) => (
            <CapabilitySlider
              key={key}
              label={label}
              icon={icon}
              description={description}
              value={capabilities[key] || 0}
              onChange={(value) => onCapabilityChange(key, value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface CapabilitySliderProps {
  label: string;
  icon: string;
  description: string;
  value: number;
  onChange: (value: number) => void;
}

function CapabilitySlider({ label, icon, description, value, onChange }: CapabilitySliderProps) {
  const labels = ['None', 'Basic', 'Average', 'Good', 'Excellent'];

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{icon}</span>
          <div>
            <div className="font-medium text-gray-900">{label}</div>
            <div className="text-xs text-gray-500">{description}</div>
          </div>
        </div>
        <div className="text-lg font-bold text-blue-600">{value}</div>
      </div>

      <div className="relative">
        <input
          type="range"
          min="0"
          max="5"
          step="1"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(value / 5) * 100}%, #e5e7eb ${(value / 5) * 100}%, #e5e7eb 100%)`
          }}
        />
        <div className="flex justify-between mt-0.5">
          <span className="text-xs text-gray-500">Poor</span>
          <span className="text-xs text-gray-500">Excellent</span>
        </div>
      </div>
    </div>
  );
}

function EndpointStep({ model, onFieldChange, onTestConnection, testingConnection, connectionStatus, error, isDuplicate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Custom Endpoint Configuration</h3>
        <p className="text-sm text-gray-500 mb-3">
          Connect your own API endpoint. We'll encrypt your API key for security.
        </p>
      </div>

      <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          🔒 Your endpoint must be OpenAI-compatible (same request/response format)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Endpoint URL *
        </label>
        <input
          type="url"
          value={model.customEndpointUrl}
          onChange={(e) => onFieldChange('customEndpointUrl', e.target.value)}
          placeholder="https://api.example.com/v1/chat/completions"
          autoComplete="new-password"
          name="endpoint-url"
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-0.5">Full URL including the path</p>
        {isDuplicate && (
          <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
            <Info size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              A model with this ID and endpoint URL already exists. You can still add it as a separate entry.
            </p>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          API Key *
        </label>
        <input
          type="password"
          value={model.customApiKey}
          onChange={(e) => onFieldChange('customApiKey', e.target.value)}
          placeholder="sk-..."
          autoComplete="new-password"
          name="api-key"
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-0.5">Will be encrypted when saved</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Custom Headers (JSON)
        </label>
        <textarea
          value={typeof model.customHeaders === 'string'
            ? model.customHeaders
            : JSON.stringify(model.customHeaders || {}, null, 2)}
          onChange={(e) => onFieldChange('customHeaders', e.target.value)}
          placeholder='{\n  "X-Custom-Header": "value"\n}'
          className="w-full p-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
        />
        <p className="text-xs text-gray-500 mt-0.5">Optional: Additional headers to include in API requests</p>
      </div>

      <div className="pt-2">
        <button
          onClick={onTestConnection}
          disabled={testingConnection || !model.customEndpointUrl || !model.customApiKey}
          className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {testingConnection ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full"></div>
              Testing...
            </>
          ) : connectionStatus === 'success' ? (
            <>
              <Check size={16} className="text-green-600" />
              Connection Successful
            </>
          ) : connectionStatus === 'error' ? (
            <>
              <X size={16} className="text-red-600" />
              Connection Failed
            </>
          ) : (
            'Test Connection'
          )}
        </button>

        {connectionStatus === 'error' && error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewStep({ model }: any) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Review Your Model</h3>
        <p className="text-sm text-gray-500 mb-6">
          Check everything looks good before saving.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <ReviewField label="Model Name" value={model.name} />
          <ReviewField label="Provider Model ID" value={model.providerModelId} />
          <ReviewField label="Provider" value={model.provider} />
          <ReviewField label="Category" value={model.category || 'Not set'} />
          <ReviewField label="Rank Position" value={model.ranked_position || 'Not set'} />
          <ReviewField label="Context Window" value={model.contextWindow || 'Not set'} />
          <ReviewField label="Max Tokens" value={model.maxTokens || 'Not set'} />
          <ReviewField label="Price/Token" value={model.price_per_token || 'Not set'} />
        </div>

        {model.description && (
          <div className="pt-4 border-t border-gray-200">
            <ReviewField label="Description" value={model.description} />
          </div>
        )}

        <div className="pt-4 border-t border-gray-200">
          <div className="font-medium text-gray-900 mb-2">Features</div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <span className={model.supports_vision ? 'text-green-600' : 'text-gray-400'}>
                {model.supports_vision ? '✓' : '○'}
              </span>
              <span className="text-gray-700">Vision</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={model.supports_streaming ? 'text-green-600' : 'text-gray-400'}>
                {model.supports_streaming ? '✓' : '○'}
              </span>
              <span className="text-gray-700">Streaming</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={model.functionCalling ? 'text-green-600' : 'text-gray-400'}>
                {model.functionCalling ? '✓' : '○'}
              </span>
              <span className="text-gray-700">Function Calling</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={model.isPrivacyCompliant ? 'text-green-600' : 'text-gray-400'}>
                {model.isPrivacyCompliant ? '✓' : '○'}
              </span>
              <span className="text-gray-700">Privacy</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={model.isUncensored ? 'text-green-600' : 'text-gray-400'}>
                {model.isUncensored ? '✓' : '○'}
              </span>
              <span className="text-gray-700">Uncensored</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={model.taskDeterminationModel ? 'text-green-600' : 'text-gray-400'}>
                {model.taskDeterminationModel ? '✓' : '○'}
              </span>
              <span className="text-gray-700">Task Det</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={model.requires_premium ? 'text-green-600' : 'text-gray-400'}>
                {model.requires_premium ? '✓' : '○'}
              </span>
              <span className="text-gray-700">Premium</span>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <div className="font-medium text-gray-900 mb-2">Capabilities</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(model.capabilities || {}).map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                <span className="font-medium">{value as number}/5</span>
              </div>
            ))}
          </div>
        </div>

        {model.provider === 'custom' && (
          <div className="pt-4 border-t border-gray-200">
            <div className="font-medium text-gray-900 mb-2">Custom Endpoint</div>
            <ReviewField label="URL" value={model.customEndpointUrl} small />
            <ReviewField label="API Key" value="••••••••••••" small />
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewField({ label, value, small }: any) {
  return (
    <div className={small ? 'text-sm' : ''}>
      <div className="text-gray-600">{label}</div>
      <div className="font-medium text-gray-900 break-words">{value || 'Not set'}</div>
    </div>
  );
}
